import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
// @ts-ignore 
import Cookies from "js-cookies";
import { jwtDecode } from "jwt-decode";

interface AuthContextType {
  token: string;
  setToken: (token: string) => void;
  departamento: string;
  setDepartamento: (departamento: string) => void;
  user: string;
  setUser: (user: string) => void;
  isAuthenticated: boolean;
  userType: string;
  setUserType: (userType: string) => void;
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  allowedPhones: string[];
  setAllowedPhones: (phones: string[]) => void;
  hasFullAccess: boolean;
  setHasFullAccess: (access: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded: any = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    const expired = decoded.exp < currentTime;

    return expired;
  } catch (error) {
    return true;
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string>(Cookies.getItem("token") || "");
  const [departamento, setDepartamento] = useState<string>(Cookies.getItem("departamento") || "");
  const [user, setUser] = useState<string>(Cookies.getItem("user") || "");
  const [userType, setUserType] = useState<string>(Cookies.getItem("userType") || "");
  const [phoneNumber, setPhoneNumber] = useState<string>(Cookies.getItem("phoneNumber") || "");
  const [allowedPhones, setAllowedPhones] = useState<string[]>(JSON.parse(Cookies.getItem("allowedPhones") || "[]"));
  const [hasFullAccess, setHasFullAccess] = useState<boolean>(Cookies.getItem("hasFullAccess") === "true");

  useEffect(() => {
    if (token) {
      Cookies.setItem("token", token);
    } else {
      Cookies.removeItem("token");
    }
  }, [token]);

  useEffect(() => {
    if (departamento) {
      Cookies.setItem("departamento", departamento);
    } else {
      Cookies.removeItem("departamento");
    }
  }, [departamento]);

  useEffect(() => {
    if (user) {
      Cookies.setItem("user", user);
    } else {
      Cookies.removeItem("user");
    }
  }, [user]);

  useEffect(() => {
    if (userType) {
      Cookies.setItem("userType", userType);
    } else {
      Cookies.removeItem("userType");
    }
  }, [userType]);

  useEffect(() => {
    if (phoneNumber) {
      Cookies.setItem("phoneNumber", phoneNumber);
    } else {
      Cookies.removeItem("phoneNumber");
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (allowedPhones.length > 0) {
      Cookies.setItem("allowedPhones", JSON.stringify(allowedPhones));
    } else {
      Cookies.removeItem("allowedPhones");
    }
  }, [allowedPhones]);

  useEffect(() => {
    Cookies.setItem("hasFullAccess", hasFullAccess.toString());
  }, [hasFullAccess]);


  const isAuthenticated = !!token && !isTokenExpired(token);

  const logout = useCallback(() => {
    setToken("");
    setDepartamento("");
    setUser("");
    setUserType("");
    setPhoneNumber("");
    setAllowedPhones([]);
    setHasFullAccess(false);

    Cookies.removeItem("token");
    Cookies.removeItem("departamento");
    Cookies.removeItem("user");
    Cookies.removeItem("userType");
    Cookies.removeItem("phoneNumber");
    Cookies.removeItem("allowedPhones");
    Cookies.removeItem("hasFullAccess");

    window.location.href = process.env.REACT_APP_AWS_START_URL || "/";
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401) {
        logout();
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        departamento,
        setDepartamento,
        user,
        setUser,
        isAuthenticated,
        userType,
        setUserType,
        phoneNumber,
        setPhoneNumber,
        allowedPhones,
        setAllowedPhones,
        hasFullAccess,
        setHasFullAccess,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("Contexto de autenticação não encontrado");
  }
  return context;
};
